import {
    BdsButton,
    BdsGrid,
    BdsIllustration,
    BdsLoadingSpinner,
    BdsRadio,
    BdsTypo,
} from 'blip-ds/dist/blip-ds-react/components';
import { AutocompleteOption } from 'blip-ds/dist/types/components/autocomplete/autocomplete-select-interface';
import { AutoComplete } from 'components/AutoComplete';
import { Modal } from 'components/Modal';
import { useAppContext } from 'contexts/AppContext';
import React, { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createToastError, createToastSuccess } from 'services/Toast';
import { TicketsResponse } from 'typings';
import { TransferMode } from 'typings/enums/TransferModeEnum';
import './styles.scss';

interface TransferTicketModalProps {
    isOpen: boolean;
    ticket: TicketsResponse['tickets'][number];
    tenantId: string;
    botId: string;
    installationId: number;
    setOpen: Dispatch<React.SetStateAction<boolean>>;
    onTicketChanged?: () => void;
}

export const TransferTicketModal: React.FC<TransferTicketModalProps> = ({
    isOpen,
    setOpen,
    ticket,
    tenantId,
    botId,
    installationId,
    onTicketChanged,
}) => {
    const { t } = useTranslation();
    const {
        appServices: { goPersonalService },
    } = useAppContext();

    const [transferMode, setTransferMode] = useState<TransferMode>(TransferMode.Queue);
    const [options, setOptions] = useState<AutocompleteOption[]>([]);
    const [selectedDestination, setSelectedDestination] = useState('');
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [isTransferring, setIsTransferring] = useState(false);

    const queueRadioRef = useRef<any>(null);
    const attendantRadioRef = useRef<any>(null);

    const fetchQueueOptions = useCallback(async () => {
        setIsLoadingOptions(true);
        try {
            const queues = await goPersonalService.getQueuesWithAgentsOnline(tenantId, botId);
            setOptions(
                queues.map(queue => ({
                    value: queue.name,
                    label: queue.name,
                    status: `${queue.agentsOnline} ${t('go-personal-transfer-modal-available-plural')}`,
                })),
            );
        } catch {
            setOptions([]);
        } finally {
            setIsLoadingOptions(false);
        }
    }, [goPersonalService, tenantId, botId, t]);

    const fetchAttendantOptions = useCallback(async () => {
        setIsLoadingOptions(true);
        try {
            const response = await goPersonalService.getAttendants(tenantId, botId);
            const seen = new Set<string>();
            const mapped: AutocompleteOption[] = [];
            for (const attendant of response.items) {
                if (!seen.has(attendant.email)) {
                    seen.add(attendant.email);
                    const online = attendant.status.toLowerCase() === 'online';
                    mapped.push({
                        value: attendant.email,
                        label: attendant.fullName,
                        status: online
                            ? t('go-personal-transfer-modal-available')
                            : t('go-personal-transfer-modal-unavailable'),
                    });
                }
            }
            setOptions(mapped);
        } catch {
            setOptions([]);
        } finally {
            setIsLoadingOptions(false);
        }
    }, [goPersonalService, tenantId, botId, t]);

    useEffect(() => {
        if (!isOpen) return;
        setSelectedDestination('');
        if (transferMode === TransferMode.Queue) {
            fetchQueueOptions();
        } else {
            fetchAttendantOptions();
        }
    }, [isOpen, transferMode, fetchQueueOptions, fetchAttendantOptions]);

    useEffect(() => {
        const addListener = (ref: React.MutableRefObject<any>, mode: TransferMode) => {
            const el = ref.current;
            if (!el) return;
            const handler = () => setTransferMode(mode);
            el.addEventListener('bdsClickChange', handler);
            return () => el.removeEventListener('bdsClickChange', handler);
        };
        const removeQueue = addListener(queueRadioRef, TransferMode.Queue);
        const removeAttendant = addListener(attendantRadioRef, TransferMode.Attendant);
        return () => {
            removeQueue?.();
            removeAttendant?.();
        };
    }, []);

    const handleTransfer = async () => {
        if (!selectedDestination) return;
        setIsTransferring(true);
        try {
            const body =
                transferMode === TransferMode.Queue
                    ? { team: selectedDestination }
                    : { team: 'DIRECT_TRANSFER', attendantEmail: selectedDestination };
            await goPersonalService.transferTicket(installationId, ticket.id, body);
            await createToastSuccess('', t('go-personal-transfer-modal-success', { ticketNumber: ticket.number }));
            setOpen(false);
            onTicketChanged?.();
        } catch {
            await createToastError(
                t('go-personal-transfer-modal-error-transfer', { ticketNumber: ticket.number }),
                t('go-personal-finish-modal-error-retry'),
            );
        } finally {
            setIsTransferring(false);
        }
    };

    const placeholder =
        transferMode === TransferMode.Queue
            ? t('go-personal-transfer-modal-select-queue')
            : t('go-personal-transfer-modal-select-attendant');

    return (
        <Modal
            size="dynamic"
            open={isOpen}
            onClose={() => setOpen(!isOpen)}
            closeButton
            outzoneClose={false}
            enterClose={false}
            footer={
                <BdsGrid style={{ width: '100%' }} justifyContent="flex-end" gap="1">
                    <BdsButton variant="secondary" onClick={() => setOpen(false)}>
                        {t('go-personal-transfer-modal-cancel')}
                    </BdsButton>
                    <BdsButton
                        variant="primary"
                        disabled={!selectedDestination || isTransferring}
                        onClick={handleTransfer}
                    >
                        {t('go-personal-transfer-modal-transfer')}
                    </BdsButton>
                </BdsGrid>
            }
        >
            <BdsGrid container gap="4">
                <BdsGrid height="200px">
                    <BdsIllustration name="paper-plane" type="spots" />
                </BdsGrid>
                <BdsGrid direction="column" gap="2" margin="t-3">
                    <BdsTypo variant="fs-20" bold="bold" className="transfer-ticket-typo">
                        {t('go-personal-transfer-modal-title', { ticketNumber: ticket?.number })}
                    </BdsTypo>

                    <BdsGrid gap="3" alignItems="center">
                        <BdsRadio
                            ref={queueRadioRef}
                            value={TransferMode.Queue}
                            label={t('go-personal-transfer-modal-queue')}
                            checked={transferMode === TransferMode.Queue}
                        />
                        <BdsRadio
                            ref={attendantRadioRef}
                            value={TransferMode.Attendant}
                            label={t('go-personal-transfer-modal-attendant')}
                            checked={transferMode === TransferMode.Attendant}
                        />
                    </BdsGrid>

                    {isLoadingOptions ? (
                        <BdsGrid justifyContent="center" padding="y-2">
                            <BdsLoadingSpinner size="small" />
                        </BdsGrid>
                    ) : (
                        <AutoComplete
                            options={options}
                            placeholder={placeholder}
                            selectedOption={selectedDestination}
                            selectionType="single"
                            handleChange={(value: string) => setSelectedDestination(value)}
                        />
                    )}
                </BdsGrid>
            </BdsGrid>
        </Modal>
    );
};
